// Smart Sash Admin Panel - Permission Gate Component
// Conditionally renders children based on permission, role, or action

import React from 'react';
import { usePermissions } from '@/hooks';
import { UserRole } from '@/models';
import { Action } from '@/config/permissions';

export interface PermissionGateProps {
  children: React.ReactNode;
  /** Render children if user has any of these permission keys (highest priority) */
  permission?: string | string[];
  /** Render children if user has any of these roles */
  roles?: UserRole[];
  /** Render children if user can perform this action */
  action?: Action;
  /** Optional fallback when denied (defaults to rendering nothing) */
  fallback?: React.ReactNode;
}

/**
 * Conditionally renders children based on permission, role, or action.
 * Priority: `permission` > `roles` > `action`.
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  roles,
  action,
  fallback = null,
}) => {
  const { hasPermission, hasRole, canPerform } = usePermissions();

  let allowed = false;
  if (permission) {
    const keys = Array.isArray(permission) ? permission : [permission];
    allowed = hasPermission(...keys);
  } else if (roles && roles.length > 0) {
    allowed = hasRole(...roles);
  } else if (action) {
    allowed = canPerform(action);
  }

  return <>{allowed ? children : fallback}</>;
};
